import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LeagueService } from "@/services/leagueService";
import { HOT_LEAGUES } from "@/constants";
import { searchLeaguesByKeyword } from "@/hooks/useRemoteLeagueSearch";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import stripLeadingGiai from "@/utils/stripLeadingGiai";

const HOT_LIMIT = 20;
const HOT_FIELDS = "id,name,logo,slug,nameVN,isHot";

export function asList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.data)) return data.data.data;
  return [];
}

export function leagueKey(league) {
  return String(league?.id ?? league?.slug ?? league?.name ?? "");
}

export function leagueLabel(league) {
  return stripLeadingGiai(league?.nameVN || league?.name || "");
}

export function sameLeague(a, b) {
  if (!a || !b) return false;
  if (a.id != null && b.id != null && String(a.id) === String(b.id)) return true;
  if (a.slug && b.slug && a.slug === b.slug) return true;
  return false;
}

export function useLeagueFilterData({
  selectedSlug,
  currentLeague,
  onSelect,
  searchOpen,
}) {
  const [hotLeagues, setHotLeagues] = useState([]);
  const [loadingHot, setLoadingHot] = useState(true);
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebouncedValue(keyword, 280).trim();
  const [remoteLeagues, setRemoteLeagues] = useState([]);
  const [searching, setSearching] = useState(false);
  const autoPickedRef = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await LeagueService.getHotLeagues({
          page: 1,
          limit: HOT_LIMIT,
          fields: HOT_FIELDS,
        });
        if (!active) return;
        const list = asList(data);
        setHotLeagues(list.length ? list : HOT_LEAGUES);
      } catch {
        if (active) setHotLeagues(HOT_LEAGUES);
      } finally {
        if (active) setLoadingHot(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (selectedSlug || autoPickedRef.current || loadingHot) return;
    const first = hotLeagues.find((l) => l?.slug);
    if (!first?.slug) return;
    autoPickedRef.current = true;
    onSelect?.(first.slug);
  }, [hotLeagues, loadingHot, onSelect, selectedSlug]);

  const isSelected = useCallback(
    (league) => {
      if (currentLeague && sameLeague(currentLeague, league)) return true;
      if (selectedSlug && league?.slug && league.slug === selectedSlug) return true;
      return false;
    },
    [currentLeague, selectedSlug],
  );

  const chips = useMemo(() => {
    const seen = new Set();
    const list = [];
    const push = (league) => {
      if (!league) return;
      const key = leagueKey(league);
      if (!key || seen.has(key)) return;
      seen.add(key);
      list.push(league);
    };
    hotLeagues.forEach(push);
    if (currentLeague && !seen.has(leagueKey(currentLeague))) {
      list.push(currentLeague);
    }
    return list;
  }, [currentLeague, hotLeagues]);

  useEffect(() => {
    if (!searchOpen || !debouncedKeyword) {
      setRemoteLeagues([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    (async () => {
      const list = asList(await searchLeaguesByKeyword(debouncedKeyword));
      if (cancelled) return;
      setRemoteLeagues(list);
      setSearching(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedKeyword, searchOpen]);

  const dropdownLeagues = debouncedKeyword ? remoteLeagues : hotLeagues;

  const selectLeague = useCallback(
    async (league) => {
      let slug = league?.slug;
      if (!slug && league?.id) {
        try {
          const full = await LeagueService.getLeagueById(league.id);
          slug = full?.slug;
        } catch {
          slug = null;
        }
      }
      if (!slug) return false;
      onSelect?.(slug);
      setKeyword("");
      return true;
    },
    [onSelect],
  );

  return {
    hotLeagues,
    loadingHot,
    keyword,
    setKeyword,
    searching,
    dropdownLeagues,
    chips,
    isSelected,
    selectLeague,
    debouncedKeyword,
  };
}
